import { LitElement, html, css } from 'lit'
import { api } from '../api.js'
import { navigate } from '../router.js'
import { toast } from './app-toast.js'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const STATE_LABEL = {
  future: 'Future',
  current: 'Today',
  needs_audit: 'Needs Audit',
  past_unreviewed: 'Needs review',
  review_complete: 'Reviewed',
  approved: 'Approved'
}
// Legend order from the design — the day's lifecycle, not the object key order —
// with each state's fill/border pair. These mirror the .future/.current/… rules
// below so a swatch always matches the cells it stands for.
const LEGEND = [
  ['future', 'var(--zg-state-future)', 'var(--neutral-100)'],
  ['current', 'var(--zg-state-current)', 'var(--sky-500)'],
  ['needs_audit', 'var(--zg-state-needs-audit)', 'var(--chartreuse-300)'],
  ['past_unreviewed', 'var(--zg-state-past)', 'var(--chartreuse-500)'],
  ['review_complete', 'var(--zg-state-review)', 'var(--eggplant-600)'],
  ['approved', 'var(--zg-state-approved)', 'var(--neutral-300)']
]

// Review calendar, restyled to the BrandExtract design system. Ziggy's original
// mustard/yellow day states have no brand equivalent, so the design maps Today
// onto sky (the strong secondary) and the two audit/review stages onto the
// chartreuse ramp light → primary. The state colors themselves live in
// index.html as --zg-state-* so the legend and cells cannot drift apart.
class PageCalendar extends LitElement {
  static properties = {
    params: { type: Array },
    _data: { state: true },
    _month: { state: true }
  }

  static styles = css`
    *, *::before, *::after { box-sizing: border-box; }

    .head { display: flex; align-items: flex-end; justify-content: space-between;
            gap: 24px; margin-bottom: 24px; }
    .eyebrow { font-family: var(--font-eyebrow); font-size: 11px; font-weight: 700;
               letter-spacing: 0.16em; text-transform: uppercase; color: var(--neutral-500);
               margin-bottom: 8px; }
    h1 { margin: 0; font-family: var(--font-display); font-size: 34px; line-height: 1.1;
         font-weight: 700; color: var(--eggplant-500); }

    .nav { display: flex; gap: 10px; flex-shrink: 0; }
    /* Outline button from the design system: squared off, eggplant rule, fills
       on hover. */
    .nav button { font-family: var(--font-sans); font-size: 14px; font-weight: 600;
                  line-height: 20px; padding: 8px 16px; cursor: pointer; border: none;
                  border-radius: 0; background: transparent; color: var(--eggplant-500);
                  box-shadow: inset 0 0 0 1px var(--eggplant-500); white-space: nowrap;
                  transition: background-color .18s ease, color .18s ease; }
    .nav button:hover { background: var(--eggplant-500); color: var(--neutral-50); }
    /* On mobile the Prev/Next buttons show just their arrow; the word returns at
       ≥640px (the accessible name stays via aria-label at every width). */
    .nav .lbl { display: none; }

    /* minmax(0, …) lets the day tracks shrink below their content width so the
       7-column grid always fits a phone. */
    .grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 10px; }
    .dow { text-align: center; font-family: var(--font-eyebrow); font-size: 11px;
           font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase;
           color: var(--neutral-500); padding-bottom: 2px; }

    /* Mobile-first: compact cells showing only the date. State is conveyed by
       color (see legend), and a chartreuse dot marks days with discovered time;
       the full label + hours live in each cell's tooltip / aria-label. */
    .cell { position: relative; min-height: 3rem; border: 1px solid var(--neutral-100);
            border-radius: 10px; padding: 10px; cursor: pointer;
            box-shadow: var(--shadow-cell); display: flex; align-items: center;
            justify-content: center; transition: box-shadow .16s ease, transform .16s ease; }
    .cell:hover { box-shadow: var(--shadow-row); transform: translateY(-1px); }
    .cell.blank { border: none; cursor: default; background: transparent; box-shadow: none; }
    .cell.blank:hover { transform: none; box-shadow: none; }
    .cell.approved { cursor: not-allowed; }
    .cell.approved:hover { transform: none; box-shadow: var(--shadow-cell); }
    .daynum { font-size: 17px; font-weight: 700; line-height: 1; font-variant-numeric: tabular-nums; }
    .tag { display: none; }
    .hours { display: none; }
    /* At-a-glance marker that Ziggy discovered time on this day. currentColor is
       the cell's own ink, so the dot stays legible on every state fill — a fixed
       color would disappear on the one whose fill it matches. */
    .dot { position: absolute; top: 6px; right: 6px; width: 8px; height: 8px; border-radius: 50%;
           background: currentColor; }

    /* Medium screens and up: enough room for the text, so restore the state label
       and discovered-hours in the cell and drop the dot (the text carries it). */
    @media (min-width: 640px) {
      .nav .lbl { display: inline; }
      .cell { min-height: 112px; padding: 12px 14px; flex-direction: column;
              align-items: flex-start; justify-content: flex-start; }
      .tag { display: block; margin-top: 8px; font-family: var(--font-eyebrow); font-size: 10px;
             font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
      .hours { display: block; margin-top: auto; font-size: 12.5px; font-weight: 600; }
      .dot { display: none; }
    }

    /* Each state sets its own fill, ink and sub-text ink — the labels and hours
       have to stay legible on both the light and the dark fills. */
    .future { background: var(--zg-state-future); color: var(--eggplant-500);
              border-color: var(--neutral-100); }
    .future .tag, .future .hours { color: var(--neutral-500); }
    .current { background: var(--zg-state-current); color: var(--white); border-color: var(--sky-500); }
    .current .tag, .current .hours { color: rgba(255,255,255,.82); }
    .needs_audit { background: var(--zg-state-needs-audit); color: var(--chartreuse-800);
                   border-color: var(--chartreuse-300); }
    .needs_audit .tag, .needs_audit .hours { color: var(--chartreuse-800); }
    .past_unreviewed { background: var(--zg-state-past); color: var(--eggplant-500);
                       border-color: var(--chartreuse-500); }
    .past_unreviewed .tag, .past_unreviewed .hours { color: var(--chartreuse-800); }
    .review_complete { background: var(--zg-state-review); color: var(--white);
                       border-color: var(--eggplant-600); }
    .review_complete .tag, .review_complete .hours { color: rgba(255,255,255,.78); }
    .approved { background: var(--zg-state-approved); color: var(--neutral-800);
                border-color: var(--neutral-300); }
    .approved .tag, .approved .hours { color: var(--neutral-700); }

    .legend { display: flex; flex-wrap: wrap; gap: 14px 28px; margin-top: 28px;
              align-items: center; }
    .legend span.item { display: flex; align-items: center; gap: 9px; font-size: 13.5px;
                        color: var(--neutral-700); }
    .legend .swatch { width: 15px; height: 15px; border-radius: 4px; display: inline-block; }

    @media (max-width: 640px) {
      .head { gap: 16px; margin-bottom: 20px; }
      h1 { font-size: 26px; }
      .grid { gap: 6px; }
    }
  `

  connectedCallback() {
    super.connectedCallback()
    this._month = (this.params && this.params[0]) || new Date().toISOString().slice(0, 7)
    this._load()
  }

  updated(changed) {
    const incoming = (this.params && this.params[0]) || new Date().toISOString().slice(0, 7)
    if (changed.has('params') && incoming !== this._month) {
      this._month = incoming
      this._load()
    }
  }

  async _load() {
    try {
      this._data = await api.get(`/calendar?month=${this._month}`)
    } catch (err) {
      toast(err.message, 'error')
    }
  }

  _shiftMonth(delta) {
    const [y, m] = this._month.split('-').map(Number)
    const d = new Date(Date.UTC(y, m - 1 + delta, 1))
    navigate(`/calendar/${d.toISOString().slice(0, 7)}`)
  }

  _open(day) {
    if (day.state === 'approved') return
    navigate(`/days/${day.date}`)
  }

  render() {
    if (!this._data) return html`<p>Loading…</p>`
    const days = this._data.days
    const leadingBlanks = new Date(`${days[0].date}T00:00:00Z`).getUTCDay()
    const [y, m] = this._month.split('-')
    const title = new Date(Date.UTC(+y, +m - 1, 1))
      .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })

    return html`
      <div class="head">
        <div>
          <div class="eyebrow">Review calendar</div>
          <h1>${title}</h1>
        </div>
        <div class="nav">
          <button @click=${() => this._shiftMonth(-1)} aria-label="Previous month">‹<span class="lbl"> Prev</span></button>
          <button @click=${() => navigate('/')}>Today</button>
          <button @click=${() => this._shiftMonth(1)} aria-label="Next month"><span class="lbl">Next </span>›</button>
        </div>
      </div>

      <div class="grid">
        ${WEEKDAYS.map(d => html`<div class="dow">${d}</div>`)}
        ${Array.from({ length: leadingBlanks }, () => html`<div class="cell blank"></div>`)}
        ${days.map(day => {
          const dayNum = Number(day.date.slice(-2))
          // Label + hours are always in the markup; CSS shows them at ≥640px and
          // swaps to just the date + dot on mobile. The tooltip / aria-label
          // carry the full meaning at every width (and for assistive tech).
          const hours = day.discoveredHours > 0 ? `, ${day.discoveredHours}h found` : ''
          const desc = `${STATE_LABEL[day.state]}${hours}`
          return html`
            <div class="cell ${day.state}" @click=${() => this._open(day)}
                 title=${desc} aria-label=${`${dayNum}: ${desc}`}>
              <span class="daynum">${dayNum}</span>
              <span class="tag">${STATE_LABEL[day.state]}</span>
              ${day.discoveredHours > 0 ? html`<span class="hours">${day.discoveredHours}h found</span>` : ''}
              ${day.discoveredHours > 0 ? html`<span class="dot"></span>` : ''}
            </div>
          `
        })}
      </div>

      <div class="legend">
        ${LEGEND.map(([state, bg, border]) => html`
          <span class="item">
            <span class="swatch" style="background:${bg};border:1px solid ${border}"></span>${STATE_LABEL[state]}
          </span>`)}
      </div>
    `
  }
}

customElements.define('page-calendar', PageCalendar)
